// product-form.js
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        // Basic DOM references
        this.form = this.querySelector('form');
        if (!this.form) {
          console.warn('product-form: form element not found inside product-form component.');
          return;
        }

        // Make sure variant id input is enabled (themes sometimes disable it)
        try {
          if (this.variantIdInput) this.variantIdInput.disabled = false;
        } catch (e) {
          // ignore
        }

        // Sync any external custom controls into hidden inputs inside the form
        this._syncExternalControls();

        // Form submit
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.form.addEventListener('submit', this.onSubmitHandler);

        // UI references
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        if (document.querySelector('cart-drawer') && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      /**
       * _syncExternalControls
       * Finds external controls rendered outside this.form and ensures their values
       * are mirrored inside the form so FormData will include them.
       *
       * Supported external attributes:
       *  - select[data-custom-dropdown-hidden-id] or select[data-custom-dropdown]
       *  - input[data-custom-text] or textarea[data-custom-text]
       *  - data-custom-input-name="properties[Custom Label]" to explicitly set hidden input name
       *  - data-custom-input-prop="Custom Label" to auto-create properties[Custom Label]
       *
       * Behavior:
       *  - If a real input/select already exists inside the form with the same name, it will be updated (no removal).
       *  - Otherwise a managed hidden input (class="custom-external-hidden") is created/updated inside the form.
       *  - Adds input/change listeners to keep values in sync.
       */
      _syncExternalControls() {
        try {
          const selector = [
            'select[data-custom-dropdown-hidden-id]',
            'select[data-custom-dropdown]',
            'input[data-custom-text]',
            'textarea[data-custom-text]'
          ].join(', ');

          const externalElements = Array.from(document.querySelectorAll(selector));
          if (!externalElements.length) return;

          externalElements.forEach((el) => {
            // Skip elements that are already inside this form
            if (this.form.contains(el)) return;

            // Name resolution: explicit name -> data-custom-input-name -> data-custom-input-prop -> element.name
            const explicitName = el.dataset.customInputName;
            const propLabel = el.dataset.customInputProp;
            const elementName = el.getAttribute('name');
            let name = explicitName || elementName || null;

            if (!name && propLabel) {
              name = `properties[${propLabel}]`;
            }
            if (!name) return;

            // Determine/prepare hidden id
            const hiddenId = el.dataset.customDropdownHiddenId || el.dataset.customInputHiddenId || ('custom-external-' + (el.id || Math.random().toString(36).slice(2)));

            // If there is an existing real form control with the same name (and it's NOT our managed hidden),
            // we will keep that control and update its value on external input events.
            const existingReal = this.form.querySelector(`[name="${CSS && CSS.escape ? CSS.escape(name) : name.replace(/["\\]/g,'\\$&')}"]`);
            if (existingReal && !existingReal.classList.contains('custom-external-hidden')) {
              const updateExisting = () => {
                if ('value' in existingReal) existingReal.value = el.value || '';
              };
              // remove previous listener if present
              if (el._customExternalListener) {
                el.removeEventListener('input', el._customExternalListener);
                el.removeEventListener('change', el._customExternalListener);
              }
              el.addEventListener('input', updateExisting);
              el.addEventListener('change', updateExisting);
              el._customExternalListener = updateExisting;
              // initial sync
              updateExisting();
              return;
            }

            // Use or create a managed hidden input inside the form
            let hidden = this.form.querySelector(`input.custom-external-hidden[id="${hiddenId}"]`);
            // Fallback find by name
            if (!hidden) {
              hidden = this.form.querySelector(`input.custom-external-hidden[name="${CSS && CSS.escape ? CSS.escape(name) : name.replace(/["\\]/g,'\\$&')}"]`);
            }
            if (!hidden) {
              hidden = document.createElement('input');
              hidden.type = 'hidden';
              hidden.id = hiddenId;
              hidden.className = 'custom-external-hidden';
              hidden.name = name;
              this.form.appendChild(hidden);
            } else {
              hidden.name = name; // keep name in sync if changed
            }

            // initial sync
            hidden.value = el.value || '';

            // update handler
            const updateHandler = () => {
              hidden.value = el.value || '';
            };

            // remove previous listeners to avoid duplicates
            if (el._customExternalListener) {
              el.removeEventListener('input', el._customExternalListener);
              el.removeEventListener('change', el._customExternalListener);
            }
            el.addEventListener('input', updateHandler);
            el.addEventListener('change', updateHandler);
            el._customExternalListener = updateHandler;
          });
        } catch (e) {
          console.warn('product-form: error syncing external controls', e);
        }
      }

      /**
       * onSubmitHandler
       * Prepares FormData including external controls, posts to cart_add_url, handles response.
       */
      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          const spinner = this.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        // Ensure external controls are synced into the form right before submit (defensive)
        this._syncExternalControls();

        // As an extra safety, also include any external elements (outside the form) we might have missed
        const externalSelector = [
          'select[data-custom-dropdown-hidden-id]',
          'select[data-custom-dropdown]',
          'input[data-custom-text]',
          'textarea[data-custom-text]'
        ].join(', ');

        const externalElements = Array.from(this.form.ownerDocument.querySelectorAll(externalSelector))
          .filter(el => !this.form.contains(el));

        externalElements.forEach((field) => {
          // Resolve name same as in _syncExternalControls
          const explicitName = field.dataset.customInputName;
          const propLabel = field.dataset.customInputProp;
          const elementName = field.getAttribute('name');
          let name = explicitName || elementName || null;
          if (!name && propLabel) name = `properties[${propLabel}]`;
          if (!name) return;

          const value = field.value || '';

          // If the form already has a real control with this name (not our managed hidden), update it and skip creating hidden
          const existingReal = this.form.querySelector(`[name="${CSS && CSS.escape ? CSS.escape(name) : name.replace(/["\\]/g,'\\$&')}"]`);
          if (existingReal && !existingReal.classList.contains('custom-external-hidden')) {
            if ('value' in existingReal) existingReal.value = value;
            return;
          }

          // If our managed hidden exists, update it; otherwise create it
          let hidden = this.form.querySelector(`input.custom-external-hidden[name="${CSS && CSS.escape ? CSS.escape(name) : name.replace(/["\\]/g,'\\$&')}"]`);
          if (!hidden) {
            hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = name;
            hidden.className = 'custom-external-hidden';
            // copy explicit hidden id when provided
            const hid = field.dataset.customDropdownHiddenId || field.dataset.customInputHiddenId;
            if (hid) hidden.id = hid;
            this.form.appendChild(hidden);
          }
          hidden.value = value;
        });

        // Build FormData and append cart sections if needed
        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (this.cart.setActiveElement) this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // Perform fetch with robust parsing (handles JSON and non-JSON responses)
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => {
            return response.text().then((text) => {
              let data;
              try {
                data = text ? JSON.parse(text) : {};
              } catch (err) {
                data = {
                  __raw: text,
                  __status: response.status,
                  __ok: response.ok
                };
              }
              data.__response = response;
              return data;
            });
          })
          .then((responseData) => {
            // Handle HTTP error status first
            const httpResponse = responseData.__response || {};
            const httpStatus = httpResponse.status || responseData.__status || 200;

            if (httpStatus >= 400) {
              const raw = responseData.__raw || responseData.description || responseData.message || 'Server returned an error';
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: raw,
                message: raw
              });
              this.handleErrorMessage(raw);
              const soldOutMessage = this.submitButton ? this.submitButton.querySelector('.sold-out-message') : null;
              if (soldOutMessage && this.submitButton) {
                this.submitButton.setAttribute('aria-disabled', true);
                if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              this.error = true;
              return;
            }

            // Handle Shopify-style error object in JSON
            if (responseData && responseData.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: responseData.errors || responseData.description,
                message: responseData.message
              });
              this.handleErrorMessage(responseData.description);

              const soldOutMessage = this.submitButton ? this.submitButton.querySelector('.sold-out-message') : null;
              if (!soldOutMessage) return;
              if (this.submitButton) this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            // Success path: if no cart drawer, redirect to cart page
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Publish update and render cart contents
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: responseData
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      if (this.cart && this.cart.renderContents) this.cart.renderContents(responseData);
                    });
                  });
                },
                { once: true }
              );
              if (quickAddModal.hide) quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                if (this.cart && this.cart.renderContents) this.cart.renderContents(responseData);
              });
            }
          })
          .catch((e) => {
            console.error('product-form add-to-cart network/parsing error:', e);
            this.handleErrorMessage('There was an error adding this product to your cart.');
          })
          .finally(() => {
            if (this.submitButton) this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && this.submitButton) this.submitButton.removeAttribute('aria-disabled');
            const spinner = this.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart';
        }
      }

      get variantIdInput() {
        return this.form ? this.form.querySelector('[name=id]') : null;
      }
    }
  );
}
